// 2D SVG renderer for the DSA visualizer — reads the same store / step-engine
// as the (now retired) 3D scene. Keeps the 3D "cube selection colors" and runs
// on the homepage-text burgundy background.
import { useDSAStore } from '../store/useDSAStore'
import { ALGORITHM_REGISTRY } from '../engine/AlgorithmRegistry'
import { STATE_COLORS } from '../shared/colors'

const BG = '#0a0a0a'

// Element "selection" colors — identical to the 3D EDGE_COLORS palette.
const STROKE = {
  inactive: '#9A5560', active: '#FBDB93', compared: '#E8A020',
  visited: '#5C6BC0', answer: '#00EE77', rejected: '#DD2222', pivot: '#DD7700',
  processing: '#D0A0FF', candidate: '#FFD080', memoized: '#80FFD8',
  frontier: '#80C8FF', pruned: '#666666', 'current-frame': '#FBDB93',
}

// Edge colors — from the 3D GraphVisualizer.
const EDGE_STROKE = {
  inactive: '#5C3036', active: '#FBDB93', compared: '#E8A020',
  visited: '#5C6BC0', answer: '#00EE77', rejected: '#DD2222',
}

// Grid / DP cells are solid-filled in 3D — keep those palettes.
const GRID_FILL = {
  inactive: '#2A1520', active: '#FBDB93', visited: '#5C6BC0', answer: '#00EE77',
  rejected: '#1A0A0E', compared: '#E8A020', frontier: '#2A6BC0', processing: '#8B5CF6',
}
const DP_FILL = {
  inactive: { bg: '#1a1a1a', text: '#444444' }, active: { bg: '#8A4010', text: '#FBDB93' },
  visited: { bg: '#3B4A80', text: '#A0AAEE' }, answer: { bg: '#006633', text: '#00EE77' },
  memoized: { bg: '#0A4A44', text: '#2ABBA0' }, compared: { bg: '#5A3A00', text: '#E8A020' },
}

const POINTER_COLORS = {
  L: '#00ff99', R: '#ff6644', i: '#00d4ff', j: '#ffd166',
  mid: '#ffffff', current: '#00d4ff', rear: '#ff6644', default: '#ccddff',
}

// ── helpers ──────────────────────────────────────────────────────────────
function hexA(hex, a) {
  const n = parseInt(hex.slice(1), 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`
}
const strokeFor = (s) => STROKE[s] || STROKE.inactive
function fillFor(s) {
  const c = STATE_COLORS[s] || STATE_COLORS.inactive
  const a = c.intensity > 0.5 ? 0.2 : c.intensity > 0.25 ? 0.12 : 0.06
  return hexA(c.base, a)
}
function glowFor(s) {
  const c = STATE_COLORS[s] || STATE_COLORS.inactive
  return c.intensity > 0.4 ? `drop-shadow(0 0 6px ${strokeFor(s)})` : 'none'
}
const scaleFor = (s) => (STATE_COLORS[s] || STATE_COLORS.inactive).scale || 1

// Shrink the value font for longer numbers so they stay inside their shape.
function valueFont(v, base) {
  const len = String(v).length
  if (len <= 1) return base
  if (len === 2) return base * 0.82
  if (len === 3) return base * 0.62
  return base * 0.48
}

const SVG_STYLE = { width: '100%', height: '100%', display: 'block' }
// Animate only cheap, GPU-friendly props (transform + color elsewhere). The glow
// (filter: drop-shadow) is applied instantly — transitioning a blur is expensive
// and can't interpolate to/from `none`, which is what made it look choppy.
const popStyle = (s) => ({
  transformBox: 'fill-box', transformOrigin: 'center',
  transform: `scale(${scaleFor(s)})`,
  transition: 'transform 0.22s ease',
  filter: glowFor(s),
})
const FONT = "'Orbitron', monospace"

function Svg({ viewBox, children }) {
  return (
    <svg viewBox={viewBox} preserveAspectRatio="xMidYMid meet" style={SVG_STYLE}>
      {children}
    </svg>
  )
}

// ── Array / Stack / Queue / Linked-list ────────────────────────────────────
const STEP = 1.55
const CELL = 1.1

function ArrayView2D({ type }) {
  const elements = useDSAStore((s) => s.elements)
  const pointers = useDSAStore((s) => s.pointers)
  const isStack = type === 'stack'
  const isLinked = type === 'linked-list'
  const n = elements.length
  if (!n) return null

  const place = (i) =>
    isStack ? { cx: 0, cy: -(i * STEP) } : { cx: (i - (n - 1) / 2) * STEP, cy: 0 }

  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
  for (let i = 0; i < n; i++) {
    const { cx, cy } = place(i)
    minX = Math.min(minX, cx - CELL / 2); maxX = Math.max(maxX, cx + CELL / 2)
    minY = Math.min(minY, cy - CELL / 2); maxY = Math.max(maxY, cy + CELL / 2)
  }
  const hasPointers = !isStack && Object.keys(pointers).length > 0
  const padTop = hasPointers ? 1.5 : 0.6
  const padBottom = 1.0
  const padRight = isLinked ? 1.8 : isStack ? 2.4 : 0.7
  const vb = `${minX - 0.7} ${minY - padTop} ${maxX - minX + 0.7 + padRight} ${maxY - minY + padTop + padBottom}`

  return (
    <Svg viewBox={vb}>
      {/* Linked-list connectors */}
      {isLinked && elements.slice(0, -1).map((_, i) => {
        const a = place(i), b = place(i + 1)
        const x1 = a.cx + CELL / 2, x2 = b.cx - CELL / 2
        return (
          <g key={`lk-${i}`}>
            <line x1={x1} y1={0} x2={x2 - 0.12} y2={0} stroke="#3A3A6A" strokeWidth={0.05} />
            <polygon points={`${x2},0 ${x2 - 0.16},-0.09 ${x2 - 0.16},0.09`} fill="#3A3A6A" />
          </g>
        )
      })}

      {/* Cells */}
      {elements.map((el, i) => {
        const { cx, cy } = place(i)
        const st = el.state
        return (
          <g key={`el-${i}`} transform={`translate(${cx} ${cy})`}>
            <g style={popStyle(st)}>
              <rect
                x={-CELL / 2} y={-CELL / 2} width={CELL} height={CELL} rx={0.12}
                fill={fillFor(st)} stroke={strokeFor(st)} strokeWidth={0.045}
                style={{ transition: 'fill 0.3s ease, stroke 0.3s ease' }}
              />
              <text
                x={0} y={0} fontSize={valueFont(el.value, 0.5)} fill={strokeFor(st)} fontWeight={700}
                textAnchor="middle" dominantBaseline="central"
                style={{ fontFamily: FONT, transition: 'fill 0.3s ease' }}
              >
                {String(el.value)}
              </text>
            </g>
            <text
              x={0} y={CELL / 2 + 0.42} fontSize={0.3} fill="#B58A82"
              textAnchor="middle" dominantBaseline="central" style={{ fontFamily: 'monospace' }}
            >
              [{i}]
            </text>
          </g>
        )
      })}

      {/* Pointers (horizontal layouts) */}
      {!isStack && Object.entries(pointers).map(([name, index]) => {
        if (index < 0 || index >= n) return null
        const { cx } = place(index)
        const col = POINTER_COLORS[name] || POINTER_COLORS.default
        const ty = -CELL / 2 - 0.35
        return (
          <g key={name} transform={`translate(${cx} 0)`} style={{ transition: 'transform 0.4s ease' }}>
            <polygon points={`0,${ty + 0.28} -0.18,${ty} 0.18,${ty}`} fill={col} />
            <text
              x={0} y={ty - 0.28} fontSize={0.38} fill={col} fontWeight={700}
              textAnchor="middle" dominantBaseline="central" style={{ fontFamily: FONT }}
            >
              {name}
            </text>
          </g>
        )
      })}

      {/* Stack "top" marker */}
      {isStack && (
        <text
          x={CELL / 2 + 0.3} y={-(n - 1) * STEP} fontSize={0.36} fill="#FBDB93"
          textAnchor="start" dominantBaseline="central" style={{ fontFamily: FONT }}
        >
          ← top
        </text>
      )}

      {/* Linked-list null terminator */}
      {isLinked && (
        <text
          x={place(n - 1).cx + CELL / 2 + 0.5} y={0} fontSize={0.36} fill="#6E4248"
          textAnchor="start" dominantBaseline="central" style={{ fontFamily: 'monospace' }}
        >
          → null
        </text>
      )}
    </Svg>
  )
}

// ── Graph / Tree / Heap ─────────────────────────────────────────────────────
const R = 0.5

function GraphView2D() {
  const nodes = useDSAStore((s) => s.nodes)
  const edges = useDSAStore((s) => s.edges)
  const nodePointers = useDSAStore((s) => s.nodePointers)
  if (!nodes.length) return null

  const byId = Object.fromEntries(nodes.map((nn) => [nn.id, nn]))
  // SVG y is flipped (world up = -y here)
  const pos = (nn) => ({ x: nn.x, y: -nn.y })

  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
  nodes.forEach((nn) => {
    const p = pos(nn)
    minX = Math.min(minX, p.x - R); maxX = Math.max(maxX, p.x + R)
    minY = Math.min(minY, p.y - R); maxY = Math.max(maxY, p.y + R)
  })
  const pad = 1.2
  const vb = `${minX - pad} ${minY - pad} ${maxX - minX + 2 * pad} ${maxY - minY + 2 * pad}`

  return (
    <Svg viewBox={vb}>
      {/* Edges */}
      {edges.map((e, i) => {
        const f = byId[e.from], t = byId[e.to]
        if (!f || !t) return null
        const a = pos(f), b = pos(t)
        const dx = b.x - a.x, dy = b.y - a.y
        const len = Math.hypot(dx, dy) || 1
        const ux = dx / len, uy = dy / len
        const sx = a.x + ux * R, sy = a.y + uy * R
        const ex = b.x - ux * R, ey = b.y - uy * R
        const col = EDGE_STROKE[e.state] || EDGE_STROKE.inactive
        const bx = ex - ux * 0.24, by = ey - uy * 0.24
        const px = -uy * 0.11, py = ux * 0.11
        return (
          <g key={`e-${i}`}>
            <line
              x1={sx} y1={sy} x2={ex} y2={ey} stroke={col} strokeWidth={0.05}
              opacity={e.state === 'inactive' ? 0.55 : 1}
              style={{ transition: 'stroke 0.3s ease' }}
            />
            {e.directed && (
              <polygon points={`${ex},${ey} ${bx + px},${by + py} ${bx - px},${by - py}`} fill={col} />
            )}
          </g>
        )
      })}

      {/* Nodes */}
      {nodes.map((nn) => {
        const p = pos(nn)
        const st = nn.state
        return (
          <g key={nn.id} transform={`translate(${p.x} ${p.y})`} style={{ transition: 'transform 0.4s ease' }}>
            <g style={popStyle(st)}>
              <circle
                r={R} fill={fillFor(st)} stroke={strokeFor(st)} strokeWidth={0.055}
                style={{ transition: 'fill 0.3s ease, stroke 0.3s ease' }}
              />
              <text
                x={0} y={0} fontSize={valueFont(nn.value, 0.46)} fill={strokeFor(st)} fontWeight={700}
                textAnchor="middle" dominantBaseline="central"
                style={{ fontFamily: FONT, transition: 'fill 0.3s ease' }}
              >
                {String(nn.value)}
              </text>
            </g>
          </g>
        )
      })}

      {/* Node pointers */}
      {Object.entries(nodePointers).map(([name, id]) => {
        const nn = byId[id]
        if (!nn) return null
        const p = pos(nn)
        return (
          <g key={name} transform={`translate(${p.x} ${p.y - 0.95})`} style={{ transition: 'transform 0.4s ease' }}>
            <polygon points={`0,0.18 -0.12,-0.05 0.12,-0.05`} fill="#FBDB93" />
            <text
              x={0} y={-0.3} fontSize={0.3} fill="#FBDB93" fontWeight={700}
              textAnchor="middle" dominantBaseline="central" style={{ fontFamily: FONT }}
            >
              {name}
            </text>
          </g>
        )
      })}
    </Svg>
  )
}

// ── Grid ────────────────────────────────────────────────────────────────────
function GridView2D() {
  const gridRows = useDSAStore((s) => s.gridRows)
  const gridCols = useDSAStore((s) => s.gridCols)
  const gridCells = useDSAStore((s) => s.gridCells)
  if (!gridRows || !gridCols) return null

  const C = 0.82, GAP = 0.06
  const totalW = gridCols * (C + GAP) - GAP
  const totalH = gridRows * (C + GAP) - GAP
  const ox = -totalW / 2, oy = -totalH / 2
  const vb = `${ox - 0.4} ${oy - 0.4} ${totalW + 0.8} ${totalH + 0.8}`

  return (
    <Svg viewBox={vb}>
      {gridCells.map((cell, idx) => {
        const row = Math.floor(idx / gridCols), col = idx % gridCols
        const x = ox + col * (C + GAP), y = oy + row * (C + GAP)
        const bg = GRID_FILL[cell.state] || GRID_FILL.inactive
        const dark = cell.state === 'inactive' || cell.state === 'rejected'
        return (
          <g key={idx}>
            <rect
              x={x} y={y} width={C} height={C} rx={0.07}
              fill={bg} stroke="rgba(255,255,255,0.06)" strokeWidth={0.02}
              style={{ transition: 'fill 0.3s ease' }}
            />
            {cell.value !== '' && cell.value !== undefined && (
              <text
                x={x + C / 2} y={y + C / 2} fontSize={0.34} fill={dark ? '#5C3036' : '#050507'}
                textAnchor="middle" dominantBaseline="central" style={{ fontFamily: FONT }}
              >
                {String(cell.value)}
              </text>
            )}
          </g>
        )
      })}
    </Svg>
  )
}

// ── DP table ──────────────────────────────────────────────────────────────
function DPView2D() {
  const dpRows = useDSAStore((s) => s.dpRows)
  const dpCols = useDSAStore((s) => s.dpCols)
  const dpCells = useDSAStore((s) => s.dpCells)
  const dpRowLabels = useDSAStore((s) => s.dpRowLabels)
  const dpColLabels = useDSAStore((s) => s.dpColLabels)
  if (!dpRows || !dpCols) return null

  const CW = 1.05, CH = 0.72, GAP = 0.07
  const totalW = dpCols * (CW + GAP) - GAP
  const totalH = dpRows * (CH + GAP) - GAP
  const ox = -totalW / 2, oy = -totalH / 2
  const vb = `${ox - 1.2} ${oy - 1.0} ${totalW + 2.0} ${totalH + 1.6}`

  return (
    <Svg viewBox={vb}>
      {dpColLabels.map((label, c) => (
        <text
          key={`cl-${c}`} x={ox + c * (CW + GAP) + CW / 2} y={oy - 0.4} fontSize={0.32} fill="#FBDB93"
          textAnchor="middle" dominantBaseline="central" style={{ fontFamily: FONT }}
        >
          {String(label)}
        </text>
      ))}
      {dpRowLabels.map((label, r) => (
        <text
          key={`rl-${r}`} x={ox - 0.45} y={oy + r * (CH + GAP) + CH / 2} fontSize={0.32} fill="#FBDB93"
          textAnchor="middle" dominantBaseline="central" style={{ fontFamily: FONT }}
        >
          {String(label)}
        </text>
      ))}
      {dpCells.map((cell, idx) => {
        const row = Math.floor(idx / dpCols), col = idx % dpCols
        const x = ox + col * (CW + GAP), y = oy + row * (CH + GAP)
        const c = DP_FILL[cell.state] || DP_FILL.inactive
        return (
          <g key={idx}>
            <rect
              x={x} y={y} width={CW} height={CH} rx={0.07}
              fill={c.bg} stroke="rgba(255,255,255,0.06)" strokeWidth={0.02}
              style={{ transition: 'fill 0.3s ease' }}
            />
            {cell.value !== '' && cell.value !== undefined && (
              <text
                x={x + CW / 2} y={y + CH / 2} fontSize={0.34} fill={c.text}
                textAnchor="middle" dominantBaseline="central" style={{ fontFamily: FONT }}
              >
                {String(cell.value)}
              </text>
            )}
          </g>
        )
      })}
    </Svg>
  )
}

// ── Router ──────────────────────────────────────────────────────────────────
export default function DSAView2D() {
  const selectedAlgorithm = useDSAStore((s) => s.selectedAlgorithm)
  const vt = ALGORITHM_REGISTRY[selectedAlgorithm]?.visualizer || 'array'

  let view
  switch (vt) {
    case 'stack': view = <ArrayView2D type="stack" />; break
    case 'queue': view = <ArrayView2D type="queue" />; break
    case 'linked-list': view = <ArrayView2D type="linked-list" />; break
    case 'graph':
    case 'tree':
    case 'heap': view = <GraphView2D />; break
    case 'grid': view = <GridView2D />; break
    case 'dp-table': view = <DPView2D />; break
    default: view = <ArrayView2D type="array" />
  }

  return (
    <div style={{ position: 'absolute', inset: 0, background: BG, pointerEvents: 'none' }}>
      {/* Inset the drawing area so content never runs under the top info overlay
          (operation / explanation / vars) or the bottom HUD. */}
      <div style={{ position: 'absolute', top: 176, bottom: 88, left: 28, right: 28 }}>
        {view}
      </div>
    </div>
  )
}
